import styled, { css } from "styled-components";
import {
  brand,
  border,
  radius,
  spacing,
  status,
  surface,
  text,
  typography,
} from "../../theme/tokens";

// ---------------------------------------------------------------------------
// Page shell
// ---------------------------------------------------------------------------

export const PageWrapper = styled.div`
  font-family: ${typography.fontFamily};
  flex: 1;
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
  height: 100%;
  overflow: hidden;
  background: ${surface.page};
`;

export const Header = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: ${spacing[4]};
  padding: ${spacing[4]} ${spacing[6]};
  background: ${surface.surface};
  border-bottom: 1px solid ${border.default};
`;

export const Title = styled.h1`
  margin: 0;
  font-size: ${typography.size.lg};
  font-weight: ${typography.weight.bold};
  color: ${text.primary};
`;

export const ScopeTabs = styled.div`
  display: flex;
  gap: ${spacing[1]};
`;

export const ScopeTab = styled.button`
  border: 1px solid ${(p) => (p.$active ? brand.primary : border.default)};
  background: ${(p) => (p.$active ? brand.primaryLight : surface.surface)};
  color: ${(p) => (p.$active ? brand.primary : text.secondary)};
  font-family: inherit;
  font-size: ${typography.size.base};
  font-weight: ${(p) =>
    p.$active ? typography.weight.semibold : typography.weight.medium};
  padding: ${spacing[1.5]} ${spacing[3]};
  border-radius: ${radius.pill};
  cursor: pointer;

  &:hover {
    border-color: ${brand.primary};
  }
`;

export const Body = styled.div`
  flex: 1;
  overflow-y: auto;
  padding: ${spacing[6]};
`;

export const Muted = styled.p`
  margin: 0;
  color: ${text.muted};
  font-size: ${typography.size.base};
`;

export const EmptyState = styled.div`
  text-align: center;
  padding: ${spacing[8]} ${spacing[4]};
  color: ${text.muted};
  font-size: ${typography.size.md};
`;

export const ErrorBanner = styled.div`
  background: ${status.failed.bg};
  border: 1px solid ${status.failed.border};
  color: ${status.failed.fg};
  border-radius: ${radius.md};
  padding: ${spacing[3]} ${spacing[4]};
  font-size: ${typography.size.base};
  margin-bottom: ${spacing[4]};
`;

// ---------------------------------------------------------------------------
// The pile
// ---------------------------------------------------------------------------

export const QueueRow = styled.button`
  width: 100%;
  display: grid;
  grid-template-columns: minmax(0, 2fr) minmax(0, 1fr) minmax(0, 3fr) auto;
  gap: ${spacing[4]};
  align-items: center;
  text-align: left;
  background: ${surface.surface};
  border: 1px solid ${border.light};
  border-radius: ${radius.lg};
  padding: ${spacing[3]} ${spacing[4]};
  margin-bottom: ${spacing[2]};
  cursor: pointer;
  font-family: inherit;

  &:hover {
    border-color: ${brand.primary};
    background: ${brand.primaryLight};
  }

  @media (max-width: 900px) {
    grid-template-columns: 1fr;
    gap: ${spacing[1]};
  }
`;

export const RowName = styled.span`
  font-size: ${typography.size.md};
  font-weight: ${typography.weight.semibold};
  color: ${text.primary};
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

export const RowWants = styled.span`
  font-size: ${typography.size.base};
  color: ${text.secondary};
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

export const RowMeta = styled.span`
  font-size: ${typography.size.sm};
  color: ${text.muted};
  white-space: nowrap;
`;

// The rating band is the strongest single predictor of the decision, so
// it reads as a chip rather than another line of grey text.
// How long someone has been waiting on the firm. Safe to colour, unlike
// the rating chip: it says nothing about the merits of the case, only
// that a person is waiting.
export const AgeChip = styled.span`
  display: inline-block;
  font-size: ${typography.size.sm};
  font-weight: ${typography.weight.semibold};
  padding: ${spacing[0.5]} ${spacing[2]};
  border-radius: ${radius.pill};
  white-space: nowrap;
  ${(p) => {
    const palette = status[p.$tone || "neutral"] || status.neutral;
    return css`
      color: ${palette.fg};
      background: ${palette.bg};
      border: 1px solid ${palette.border};
    `;
  }}
`;

export const RatingChip = styled.span`
  display: inline-block;
  font-size: ${typography.size.sm};
  font-weight: ${typography.weight.semibold};
  padding: ${spacing[0.5]} ${spacing[2]};
  border-radius: ${radius.pill};
  white-space: nowrap;
  ${(p) => {
    const tone = p.$tone || "neutral";
    const palette = status[tone] || status.neutral;
    return css`
      color: ${palette.fg};
      background: ${palette.bg};
      border: 1px solid ${palette.border};
    `;
  }}
`;

// ---------------------------------------------------------------------------
// The card
// ---------------------------------------------------------------------------

export const CardWrapper = styled.div`
  max-width: 1400px;
  margin: 0 auto;
`;

// Card on the left, decision on the right (Matthew, 2026-09-18). It was
// a sticky bar across the bottom, which covered the discharge, prior
// representation and combat rows — the last three of the twelve facts
// the decision is supposed to rest on. A panel that hides the evidence
// while asking for the verdict is worse than one that scrolls.
export const CardLayout = styled.div`
  display: grid;
  grid-template-columns: minmax(0, 1fr) 380px;
  gap: ${spacing[5]};
  align-items: start;

  @media (max-width: 1100px) {
    grid-template-columns: minmax(0, 1fr);
  }
`;

export const CardMain = styled.div`
  min-width: 0;
`;

export const CardAside = styled.aside`
  position: sticky;
  // Clears the page header; the panel then travels with the reader.
  top: ${spacing[2]};
  min-width: 0;

  @media (max-width: 1100px) {
    position: static;
  }
`;

export const BackLink = styled.button`
  border: none;
  background: none;
  color: ${brand.primary};
  font-family: inherit;
  font-size: ${typography.size.base};
  font-weight: ${typography.weight.medium};
  padding: 0;
  margin-bottom: ${spacing[3]};
  cursor: pointer;

  &:hover {
    text-decoration: underline;
  }
`;

export const CardHead = styled.div`
  background: ${surface.surface};
  border: 1px solid ${border.light};
  border-radius: ${radius.lg} ${radius.lg} 0 0;
  padding: ${spacing[5]} ${spacing[6]};
`;

export const ClientName = styled.h2`
  margin: 0 0 ${spacing[1]} 0;
  font-size: ${typography.size.xl};
  font-weight: ${typography.weight.bold};
  color: ${text.primary};
`;

export const HeadMeta = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: ${spacing[2]};
  align-items: center;
  font-size: ${typography.size.base};
  color: ${text.muted};
`;

// The service picture, directly under the name (Matthew, 2026-09-18).
// Branch and dates are what every exposure presumption is checked
// against, so they sit above everything else on the card.
export const ServiceStrip = styled.div`
  background: ${surface.folderTint};
  border: 1px solid ${border.light};
  border-top: none;
  padding: ${spacing[3]} ${spacing[6]};
`;

export const ServiceHeadline = styled.div`
  font-size: ${typography.size.md};
  font-weight: ${typography.weight.semibold};
  color: ${text.primary};
`;

export const ServiceServed = styled.div`
  margin-top: ${spacing[1]};
  font-size: ${typography.size.base};
  color: ${text.secondary};
  white-space: pre-wrap;
  word-break: break-word;

  // Labelled as intake's words, not as a finding. The boxes this comes
  // from are misleadingly named — the one called Thailand has held "He
  // was stationed Germany" — so the card reports and the attorney
  // judges.
  strong {
    font-weight: ${typography.weight.medium};
    color: ${text.muted};
  }
`;

export const Section = styled.section`
  background: ${surface.surface};
  border: 1px solid ${border.light};
  border-top: none;
  padding: ${spacing[4]} ${spacing[6]};

  &:last-of-type {
    border-radius: 0 0 ${radius.lg} ${radius.lg};
  }
`;

export const SectionTitle = styled.h3`
  margin: 0 0 ${spacing[3]} 0;
  font-size: ${typography.size.sm};
  font-weight: ${typography.weight.bold};
  text-transform: uppercase;
  letter-spacing: 0.04em;
  color: ${text.tertiary};
`;

// The dozen facts that drive the decision. Two columns so the whole set
// is visible without scrolling on a laptop — the point of the redesign is
// that the reviewer should not have to hunt.
export const LeadGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
  gap: ${spacing[3]} ${spacing[6]};
`;

export const Fact = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${spacing[0.5]};
  padding-bottom: ${spacing[2]};
  border-bottom: 1px solid ${border.light};
`;

export const FactLabel = styled.span`
  font-size: ${typography.size.sm};
  color: ${text.muted};
`;

export const FactValue = styled.span`
  font-size: ${typography.size.md};
  font-weight: ${typography.weight.medium};
  color: ${(p) => (p.$absent ? text.disabled : text.primary)};
  font-style: ${(p) => (p.$absent ? "italic" : "normal")};
  white-space: pre-wrap;
  word-break: break-word;
`;

export const Collapsible = styled.div`
  border-top: 1px solid ${border.light};

  &:first-of-type {
    border-top: none;
  }
`;

export const CollapsibleHeader = styled.button`
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: ${spacing[2]};
  background: none;
  border: none;
  padding: ${spacing[3]} 0;
  cursor: pointer;
  font-family: inherit;
  font-size: ${typography.size.md};
  font-weight: ${typography.weight.semibold};
  color: ${text.primary};
  text-align: left;

  &:hover {
    color: ${brand.primary};
  }
`;

export const CollapsibleCount = styled.span`
  font-size: ${typography.size.sm};
  font-weight: ${typography.weight.regular};
  color: ${text.muted};
`;

export const RowList = styled.dl`
  margin: 0 0 ${spacing[3]} 0;
  display: grid;
  grid-template-columns: minmax(200px, 1fr) minmax(0, 2fr);
  gap: ${spacing[2]} ${spacing[4]};

  @media (max-width: 720px) {
    grid-template-columns: 1fr;
    gap: ${spacing[0.5]};
  }
`;

export const RowLabel = styled.dt`
  margin: 0;
  font-size: ${typography.size.base};
  color: ${text.muted};
`;

export const RowValue = styled.dd`
  margin: 0;
  font-size: ${typography.size.base};
  color: ${text.primary};
  white-space: pre-wrap;
  word-break: break-word;
`;

export const QuestionBlock = styled.div`
  background: ${status.review.bg};
  border: 1px solid ${status.review.border};
  border-radius: ${radius.md};
  padding: ${spacing[3]} ${spacing[4]};
  margin-bottom: ${spacing[3]};
  font-size: ${typography.size.base};
  color: ${text.primary};

  strong {
    display: block;
    color: ${status.review.fg};
    margin-bottom: ${spacing[0.5]};
  }
`;

export const SummaryBlock = styled.pre`
  margin: 0;
  font-family: inherit;
  font-size: ${typography.size.base};
  color: ${text.secondary};
  white-space: pre-wrap;
  word-break: break-word;
  background: ${surface.folderTint};
  border: 1px solid ${border.light};
  border-radius: ${radius.md};
  padding: ${spacing[3]} ${spacing[4]};
`;

// ---------------------------------------------------------------------------
// The decision
// ---------------------------------------------------------------------------

// No longer sticky itself — the aside column owns that, so the panel
// scrolls with its own content when it is taller than the viewport
// instead of clipping the buttons out of reach.
export const DecisionBarWrapper = styled.div`
  background: ${surface.surface};
  border: 1px solid ${border.default};
  border-radius: ${radius.lg};
  padding: ${spacing[4]} ${spacing[5]};
  box-shadow: 0 1px 12px rgba(27, 32, 40, 0.06);
  max-height: calc(100vh - ${spacing[4]});
  overflow-y: auto;

  @media (max-width: 1100px) {
    margin-top: ${spacing[4]};
    max-height: none;
  }
`;

export const NoteLabel = styled.label`
  display: block;
  font-size: ${typography.size.base};
  font-weight: ${typography.weight.medium};
  color: ${text.primary};
  margin-bottom: ${spacing[1]};
`;

export const NoteHint = styled.span`
  display: block;
  font-size: ${typography.size.sm};
  font-weight: ${typography.weight.regular};
  color: ${text.muted};
  margin-bottom: ${spacing[2]};
`;

export const NoteInput = styled.textarea`
  width: 100%;
  box-sizing: border-box;
  min-height: 72px;
  resize: vertical;
  font-family: inherit;
  font-size: ${typography.size.md};
  color: ${text.primary};
  padding: ${spacing[2.5]} ${spacing[3]};
  border-radius: ${radius.md};
  border: 1px solid
    ${(p) => (p.$invalid ? status.failed.border : border.default)};
  background: ${surface.surface};

  &:focus {
    outline: none;
    border-color: ${brand.primary};
    box-shadow: 0 0 0 3px ${brand.primaryLight};
  }
`;

export const TextInput = styled.input`
  width: 100%;
  box-sizing: border-box;
  font-family: inherit;
  font-size: ${typography.size.md};
  color: ${text.primary};
  padding: ${spacing[2.5]} ${spacing[3]};
  border-radius: ${radius.md};
  border: 1px solid
    ${(p) => (p.$invalid ? status.failed.border : border.default)};

  &:focus {
    outline: none;
    border-color: ${brand.primary};
    box-shadow: 0 0 0 3px ${brand.primaryLight};
  }
`;

export const ButtonRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: ${spacing[2]};
  align-items: center;
  margin-top: ${spacing[4]};
`;

export const Spacer = styled.span`
  flex: 1;
`;

// The outcomes stack in the narrow column so each is a full-width
// target and the order reads as a decision: take it, refuse it, or ask.
export const ButtonStack = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${spacing[2]};
  margin-top: ${spacing[4]};

  > button {
    width: 100%;
  }
`;

export const ButtonPair = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: ${spacing[2]};

  > button {
    width: 100%;
  }
`;

// Park and hand-over are queue mechanics, not decisions, so they sit
// below a rule and read quieter.
export const QueueActions = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: ${spacing[2]};
  margin-top: ${spacing[3]};
  padding-top: ${spacing[3]};
  border-top: 1px solid ${border.light};

  > button {
    width: 100%;
    font-weight: ${typography.weight.medium};
  }
`;

const buttonBase = css`
  font-family: inherit;
  font-size: ${typography.size.md};
  font-weight: ${typography.weight.semibold};
  padding: ${spacing[2.5]} ${spacing[5]};
  border-radius: ${radius.md};
  cursor: pointer;
  border: 1px solid transparent;

  &:disabled {
    opacity: 0.55;
    cursor: not-allowed;
  }
`;

export const SignUpButton = styled.button`
  ${buttonBase};
  background: ${status.ready.fg};
  color: ${text.onAccent};

  &:hover:not(:disabled) {
    filter: brightness(1.08);
  }
`;

export const TurnDownButton = styled.button`
  ${buttonBase};
  background: ${surface.surface};
  color: ${status.failed.fg};
  border-color: ${status.failed.border};

  &:hover:not(:disabled) {
    background: ${status.failed.bg};
  }
`;

export const SecondaryButton = styled.button`
  ${buttonBase};
  background: ${surface.surface};
  color: ${text.secondary};
  border-color: ${border.default};

  &:hover:not(:disabled) {
    border-color: ${brand.primary};
    color: ${brand.primary};
  }
`;

export const DecidedBanner = styled.div`
  background: ${status.ready.bg};
  border: 1px solid ${status.ready.border};
  color: ${status.ready.fg};
  border-radius: ${radius.md};
  padding: ${spacing[3]} ${spacing[4]};
  font-size: ${typography.size.md};
  font-weight: ${typography.weight.medium};
  margin-bottom: ${spacing[3]};
`;

// The system's guess, shown only after the decision is on record. Agreeing
// is deliberately quiet; a difference is amber, because that is the case
// worth a sentence from the reviewer. Nothing here is red or green — a
// difference is not a mistake by either side.
export const RevealPanel = styled.div`
  background: ${(p) => (p.$differs ? status.review.bg : surface.page)};
  border: 1px solid
    ${(p) => (p.$differs ? status.review.border : border.default)};
  border-radius: ${radius.md};
  padding: ${spacing[3]} ${spacing[4]};
  margin-bottom: ${spacing[4]};
`;

export const RevealHeadline = styled.div`
  font-size: ${typography.size.md};
  font-weight: ${typography.weight.medium};
  color: ${(p) => (p.$differs ? status.review.fg : text.primary)};
  margin-bottom: ${spacing[1]};
`;

export const RevealDetail = styled.div`
  font-size: ${typography.size.sm};
  color: ${text.muted};
  margin-top: ${spacing[1]};
`;

// Confirmation that an action landed. Without it the card simply
// vanished, which is half of why a question that reached nobody was
// reported as "it didn't do anything".
export const FlashBanner = styled.div`
  background: ${status.ready.bg};
  border: 1px solid ${status.ready.border};
  color: ${status.ready.fg};
  border-radius: ${radius.md};
  padding: ${spacing[2.5]} ${spacing[4]};
  font-size: ${typography.size.base};
  font-weight: ${typography.weight.medium};
  margin-bottom: ${spacing[4]};
`;

// Qualifies the fact above it. Used for the kind of discharge when it
// is not honorable — general under honorable usually keeps VA
// eligibility, other than honorable can bar it, and a bare "No" hides
// the difference that decides whether there is a case at all.
export const FactDetail = styled.span`
  font-size: ${typography.size.sm};
  color: ${status.review.fg};
  background: ${status.review.bg};
  border: 1px solid ${status.review.border};
  border-radius: ${radius.sm};
  padding: ${spacing[0.5]} ${spacing[1.5]};
  margin-top: ${spacing[0.5]};
  align-self: flex-start;
  white-space: pre-wrap;
  word-break: break-word;
`;

// What the veteran is paid beyond the bare percentage — TDIU today — the
// way the Salesforce matter page shows it beside the combined rating.
// Neutral like the rating chip: the card never colours a sign-up signal.
export const RatingCompanion = styled.span`
  font-size: ${typography.size.sm};
  color: ${text.secondary};
  white-space: pre-wrap;
  word-break: break-word;

  strong {
    color: ${text.primary};
    font-weight: ${typography.weight.semibold};
    margin-right: ${spacing[1]};
  }
`;

// ---------------------------------------------------------------------------
// The personal-injury card (Matthew, 2026-10-31)
// ---------------------------------------------------------------------------
// Built for a phone first: one column, the summary and the five lights
// above the fold, the facts grouped and collapsed, and the decision bar
// pinned to the bottom of the screen on a narrow viewport. On a desktop
// it falls into the same two-column shape as the VA card.

export const PiLayout = styled.div`
  display: grid;
  grid-template-columns: minmax(0, 1fr) 380px;
  gap: ${spacing[5]};
  align-items: start;

  @media (max-width: 900px) {
    grid-template-columns: minmax(0, 1fr);
    gap: ${spacing[3]};
  }
`;

export const PiMain = styled.div`
  min-width: 0;
  // Room for the pinned bar so the last section is never hidden under it.
  @media (max-width: 900px) {
    padding-bottom: ${spacing[4]};
  }
`;

export const PiAside = styled.aside`
  position: sticky;
  top: ${spacing[2]};
  min-width: 0;

  @media (max-width: 900px) {
    position: sticky;
    top: auto;
    bottom: 0;
    z-index: 5;
    margin: 0 -${spacing[3]};
  }
`;

export const PiBar = styled.div`
  background: ${surface.surface};
  border: 1px solid ${border.default};
  border-radius: ${radius.lg};
  padding: ${spacing[4]};

  @media (max-width: 900px) {
    border-radius: ${radius.lg} ${radius.lg} 0 0;
    border-bottom: none;
    padding: ${spacing[3]} ${spacing[3]} ${spacing[4]};
    box-shadow: 0 -4px 16px rgba(27, 32, 40, 0.08);
  }
`;

export const PiHead = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${spacing[1]};
  margin-bottom: ${spacing[3]};
`;

export const PiName = styled.h2`
  margin: 0;
  font-size: 24px;
  line-height: 1.2;
  font-weight: ${typography.weight.semibold};
  color: ${text.primary};
`;

export const PiMeta = styled.div`
  font-size: ${typography.size.md};
  color: ${text.secondary};
`;

export const PiFine = styled.div`
  font-size: ${typography.size.sm};
  color: ${text.muted};
`;

// The one line he reads before he taps.
export const PiSummary = styled.p`
  margin: 0 0 ${spacing[3]};
  font-size: 17px;
  line-height: 1.45;
  font-weight: ${typography.weight.medium};
  color: ${text.primary};
`;

export const PiLights = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: ${spacing[2]} ${spacing[3]};
  margin-bottom: ${spacing[2]};
`;

const lightColor = (state) =>
  state === "pass"
    ? status.ready.fg
    : state === "fail"
    ? status.failed.fg
    : state === "watch"
    ? status.review.fg
    : status.neutral.border;

export const PiLight = styled.span`
  display: inline-flex;
  align-items: center;
  gap: ${spacing[1.5]};
  font-size: ${typography.size.md};
  color: ${(p) => (p.$state === "pass" ? text.secondary : text.primary)};
  font-weight: ${(p) =>
    p.$state === "pass" ? typography.weight.regular : typography.weight.medium};

  &::before {
    content: "";
    width: 11px;
    height: 11px;
    border-radius: 50%;
    background: ${(p) => lightColor(p.$state)};
    flex: 0 0 auto;
  }
`;

export const PiLightNote = styled.div`
  font-size: ${typography.size.sm};
  color: ${text.secondary};
  margin-bottom: ${spacing[2]};
`;

export const PiFlags = styled.div`
  background: ${status.review.bg};
  border: 1px solid ${status.review.border};
  border-radius: ${radius.md};
  padding: ${spacing[2.5]} ${spacing[3]};
  margin: ${spacing[3]} 0;
  font-size: ${typography.size.md};
  color: ${text.primary};

  strong {
    display: block;
    color: ${status.review.fg};
    font-size: ${typography.size.sm};
    text-transform: uppercase;
    letter-spacing: 0.04em;
    margin-bottom: ${spacing[1]};
  }

  div + div {
    margin-top: ${spacing[1]};
  }
`;

export const PiCallBlock = styled.div`
  background: ${status.processing.bg};
  border: 1px solid ${status.processing.border};
  border-radius: ${radius.md};
  padding: ${spacing[2.5]} ${spacing[3]};
  margin: ${spacing[3]} 0;
  font-size: ${typography.size.md};
  color: ${text.primary};

  strong {
    color: ${status.processing.fg};
  }
`;

// A fact whose answer is "the script never asked" reads in amber italics,
// so what is missing is as visible as what is there.
export const PiRowValue = styled.dd`
  margin: 0;
  font-size: ${typography.size.md};
  color: ${(p) => (p.$missing ? status.review.fg : text.primary)};
  font-style: ${(p) => (p.$missing ? "italic" : "normal")};
  white-space: pre-wrap;
  word-break: break-word;
`;

export const PiNotes = styled.pre`
  margin: 0;
  font-family: inherit;
  font-size: ${typography.size.md};
  line-height: 1.5;
  color: ${text.secondary};
  white-space: pre-wrap;
  word-break: break-word;
`;

// Tap chips: the turn-down reasons as the source org spells them, sized
// for a thumb.
export const ChipRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: ${spacing[2]};
  margin: ${spacing[2]} 0;
`;

export const Chip = styled.button`
  font-family: inherit;
  font-size: ${typography.size.md};
  font-weight: ${typography.weight.medium};
  min-height: 40px;
  padding: ${spacing[2]} ${spacing[3]};
  border-radius: 999px;
  border: 1px solid ${(p) => (p.$active ? status.failed.fg : border.default)};
  background: ${(p) => (p.$active ? status.failed.bg : surface.surface)};
  color: ${(p) => (p.$active ? status.failed.fg : text.primary)};
  cursor: pointer;

  &:hover:not(:disabled) {
    border-color: ${status.failed.border};
    background: ${status.failed.bg};
  }

  &:disabled {
    opacity: 0.55;
    cursor: not-allowed;
  }
`;

// The thumb bar: the primary pair big and side by side, the rest in a
// row beneath, every target at least 44px tall.
export const ThumbPair = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: ${spacing[2]};

  > button {
    width: 100%;
    min-height: 48px;
    font-size: 16px;
  }
`;

export const ThumbRow = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: ${spacing[2]};
  margin-top: ${spacing[2]};

  > button {
    width: 100%;
    min-height: 44px;
    font-size: ${typography.size.md};
    font-weight: ${typography.weight.medium};
    padding-left: ${spacing[2]};
    padding-right: ${spacing[2]};
  }
`;

export const BarNote = styled.textarea`
  width: 100%;
  min-height: 64px;
  margin-top: ${spacing[2]};
  padding: ${spacing[2]} ${spacing[3]};
  border: 1px solid ${(p) => (p.$invalid ? status.failed.border : border.default)};
  border-radius: ${radius.md};
  font-family: inherit;
  font-size: 16px;
  color: ${text.primary};
  resize: vertical;
  box-sizing: border-box;
`;

export const BarLabel = styled.div`
  font-size: ${typography.size.md};
  font-weight: ${typography.weight.medium};
  color: ${text.primary};
`;

export const BarHint = styled.div`
  font-size: ${typography.size.sm};
  color: ${text.muted};
  margin-top: ${spacing[0.5]};
`;

// Queue rows for PI intakes: the summary instead of a rating.
export const RowSummary = styled.span`
  grid-column: 1 / -1;
  font-size: ${typography.size.md};
  color: ${text.secondary};
  line-height: 1.4;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
`;

export const RowLights = styled.span`
  display: inline-flex;
  gap: 5px;
  align-items: center;
`;

export const RowLightDot = styled.span`
  width: 9px;
  height: 9px;
  border-radius: 50%;
  background: ${(p) => lightColor(p.$state)};
  display: inline-block;
`;

export const PracticeChip = styled.span`
  display: inline-block;
  font-size: ${typography.size.sm};
  font-weight: ${typography.weight.medium};
  color: ${status.processing.fg};
  background: ${status.processing.bg};
  border: 1px solid ${status.processing.border};
  border-radius: 999px;
  padding: 2px 8px;
  white-space: nowrap;
`;
